import { AudienceType } from '@pelckmans/business-components/types/Audiences';
import { MedialinkKind } from '@pelckmans/business-components/types/Medialink';
import { RangeValue } from '@pelckmans/business-components/types/Range';
import { useContext, useMemo } from 'react';
import { Medialink } from '../_api/MedialinksApi';
import { UserMaterial, isLti } from '../_api/UserMaterialApi';
import MedialinksContext from '../_contexts/MedialinksContext';
import ModuleContext from '../_contexts/ModuleContext';
import UserMaterialContext from '../_contexts/UserMaterialContext';
import BulkShareContext from '../browse/contexts/BulkShareContext';
import useMedialinks from './useMedialinks';
import useUserMaterial from './useUserMaterial';
import { checkIsExercise } from '../../../utils/medialink';

export type ShareInfo = {
  id: string;
  moduleId: string;
  nodeId: string;
  materialType: string;
  materialId: string;
  audiences: AudienceInfo[];
};

export type AssignmentInfo = {
  id: string;
  from: string;
  to: string;
  repeatable: boolean;
  timezone: string;
  initialFrom?: string;
};

export type AudienceInfo = {
  audienceType: AudienceType;
  solutionRange?: RangeValue;
  schoolId: string;
  groupId?: string;
  userId?: string;
  assignment?: AssignmentInfo;
};

export default function useBulkSharing() {
  const {
    multiSelectModeEnabled,
    setMultiSelectModeEnabled,
    selectedUserMaterial: selectedUserMaterialIds,
    setSelectedUserMaterial,
    selectedMedialinks: selectedMedialinkIds,
    setSelectedMedialinks,
  } = useContext(BulkShareContext);

  const { userMaterial } = useUserMaterial();
  const { setUserMaterial } = useContext(UserMaterialContext);
  const { medialinks } = useMedialinks();
  const { setMedialinks } = useContext(MedialinksContext);

  const { selectedModule } = useContext(ModuleContext);

  const selectedUserMaterial = useMemo(
    () => userMaterial.filter(x => selectedUserMaterialIds.includes(x.id)),
    [selectedUserMaterialIds, userMaterial],
  );

  const selectedMedialinks = useMemo(
    () => medialinks.filter(x => selectedMedialinkIds.includes(x.id)),
    [medialinks, selectedMedialinkIds],
  );

  const isShareButtonEnabled = useMemo(() => {
    return multiSelectModeEnabled && (selectedUserMaterialIds.length > 0 || selectedMedialinkIds.length > 0);
  }, [multiSelectModeEnabled, selectedMedialinkIds, selectedUserMaterialIds]);

  const getHeaderCheckboxState = (selectedItems: string[], items: typeof userMaterial | typeof medialinks) => {
    if (selectedItems.length === 0) return 'unchecked';
    if (items.length === selectedItems.length) return 'checked';
    return 'indeterminate';
  };

  const getUserMaterialHeaderCheckboxState = (filteredUserMaterial: UserMaterial[]) =>
    getHeaderCheckboxState(selectedUserMaterialIds, filteredUserMaterial);

  const getMedialinksHeaderCheckboxState = (filteredMedialinks: Medialink[]) =>
    getHeaderCheckboxState(selectedMedialinkIds, filteredMedialinks);

  const getMedialinksByCategoryHeaderCheckboxState = (ids: string[]) => {
    if (ids.every(x => selectedMedialinkIds.includes(x))) return 'checked';
    if (ids.some(x => selectedMedialinkIds.includes(x))) return 'indeterminate';

    return 'unchecked';
  };

  const toggleUserMaterial = (id: string) => {
    if (selectedUserMaterialIds.includes(id)) {
      setSelectedUserMaterial(selectedUserMaterialIds.filter(x => x !== id));
    } else {
      setSelectedUserMaterial([...selectedUserMaterialIds, id]);
    }
  };

  const toggleMedialink = (id: string) => {
    if (selectedMedialinkIds.includes(id)) {
      setSelectedMedialinks(selectedMedialinkIds.filter(x => x !== id));
    } else {
      setSelectedMedialinks([...selectedMedialinkIds, id]);
    }
  };

  const selectAllUserMaterial = (material: UserMaterial[]) => {
    setSelectedUserMaterial(material.map(x => x.id));
  };

  const deselectAllUserMaterial = () => {
    setSelectedUserMaterial([]);
  };

  const selectAllMedialinks = (medialinks: Medialink[]) => {
    setSelectedMedialinks(medialinks.map(x => x.id));
  };

  const deselectAllMedialinks = () => {
    setSelectedMedialinks([]);
  };

  const materialToShare: {
    userMaterials: {
      id: string;
      name: string;
      isExercise: boolean;
      shared: boolean;
      kind: 'user-material';
    }[];
    mediaLinks: {
      id: string;
      name: string;
      kind: MedialinkKind;
      shared: boolean;
    }[];
  } = useMemo(() => {
    return {
      userMaterials: selectedUserMaterial.map(item => ({
        id: item.id,
        name: item.name,
        isExercise: Boolean(isLti(item) && item.userMaxScore),
        shared: Boolean(item.shared || item.assignment),
        kind: 'user-material',
      })),
      mediaLinks: selectedMedialinks.map(item => ({
        id: item.id,
        name: item.name,
        kind: item.kind,
        shared: item.shares.length > 0 || item.assignments.length > 0,
      })),
    };
  }, [selectedMedialinks, selectedUserMaterial]);

  const mixedContentNames = useMemo(() => {
    const nonExerciseUserMaterial = selectedUserMaterial.filter(material => !isLti(material) || !material.userMaxScore);
    const nonExerciseMedialinks = selectedMedialinks.filter(({ kind }) => !checkIsExercise(kind));

    return [...nonExerciseUserMaterial, ...nonExerciseMedialinks].map(({ name }) => name);
  }, [selectedMedialinks, selectedUserMaterial]);

  const overrideSharesNames = useMemo(() => {
    const sharedUserMaterial = selectedUserMaterial.filter(({ shared, assignment }) => shared || assignment);

    const sharedMedialinks = selectedMedialinks.filter(
      ({ shares = [], assignments = [] }) => shares.length > 0 || assignments.length > 0,
    );

    return [...sharedUserMaterial, ...sharedMedialinks].map(({ name }) => name);
  }, [selectedUserMaterial, selectedMedialinks]);

  const hasDHLKOrOnlySharedUserMaterials =
    selectedModule?.licenses.hasDigitalManualLicenseForTeacher ||
    (selectedMedialinks.length === 0 && !selectedUserMaterial.find(material => !material.shared));

  const stopSharingSelected = () => {
    if (selectedUserMaterial.length > 0) {
      setUserMaterial(
        userMaterial.map(um => {
          if (selectedUserMaterial.find(x => x.id === um.id)) {
            delete um.shared;
            delete um.assignment;
          }
          return um;
        }),
      );
    }

    if (selectedMedialinks.length > 0) {
      setMedialinks(
        medialinks.map(ml => {
          if (selectedMedialinks.find(x => x.id === ml.id)) {
            ml.shares = [];
            ml.assignments = [];
          }
          return ml;
        }),
      );
    }
  };

  const startSharingSelected = (shareInfo: ShareInfo[]) => {
    const shareInfoForUserMaterial = shareInfo.filter(info => info.materialType === 'user-material');

    if (shareInfoForUserMaterial.length > 0) {
      setUserMaterial(
        userMaterial.map(material => {
          const shareInfo = shareInfoForUserMaterial.find(info => info.materialId === material.id);

          if (shareInfo) {
            delete material.shared;
            delete material.assignment;

            const { audiences } = shareInfo;
            const assignmentAudience = audiences?.find(x => x.assignment);
            if (assignmentAudience) material.assignment = assignmentAudience.assignment;

            const shareAudience = audiences?.find(x => !x.assignment);
            if (shareAudience) material.shared = true;
          }

          return material;
        }),
      );
    }

    const shareInfoForMedialinks = shareInfo.filter(info => info.materialType === 'medialink');

    if (shareInfoForMedialinks.length > 0) {
      setMedialinks(
        medialinks.map(medialink => {
          const shareInfo = shareInfoForMedialinks.find(info => info.materialId === medialink.id);

          if (shareInfo) {
            const hasShares = shareInfo.audiences?.find(x => !x.assignment);

            medialink.shares = hasShares ? [{ id: shareInfo.id }] : [];

            // Due to bulk API design,
            // we chose to determine assignments for every medialink
            const assignments = shareInfo.audiences.filter(x => x.assignment).map(audience => audience.assignment);

            medialink.assignments = assignments as AssignmentInfo[];
          }

          return medialink;
        }),
      );
    }
  };

  const onBulkShare = (shareInfo?: ShareInfo[] | undefined) => {
    if (!shareInfo) {
      stopSharingSelected();
    } else {
      startSharingSelected(shareInfo);
    }
    setMultiSelectModeEnabled(false);
    setSelectedUserMaterial([]);
    setSelectedMedialinks([]);
  };

  return {
    multiSelectModeEnabled,
    setMultiSelectModeEnabled,
    selectedUserMaterial,
    setSelectedUserMaterial,
    selectedMedialinks,
    setSelectedMedialinks,
    getUserMaterialHeaderCheckboxState,
    getMedialinksHeaderCheckboxState,
    getMedialinksByCategoryHeaderCheckboxState,
    isShareButtonEnabled,
    selectAllUserMaterial,
    deselectAllUserMaterial,
    selectAllMedialinks,
    deselectAllMedialinks,
    toggleUserMaterial,
    toggleMedialink,
    materialToShare,
    mixedContentNames,
    overrideSharesNames,
    hasDHLKOrOnlySharedUserMaterials,
    onBulkShare,
  };
}
